import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Earnings Call arc — your "MyCo Holdings" reports.
 *
 * Gated on hostile takeover completed OR ownsYacht (signals: you have a thing
 * to report on). 4 analyst questions decide guidance score → tape moves.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns guidance score delta
}

const BEATS: Beat[] = [
  {
    text: 'ANALYST 1: "can you walk us through Q3 organic growth?"',
    options: [
      { label: '"organic growth was 14%."', effect: () => { return 1; } },
      { label: '"we don\'t talk about organic anymore. we talk about \"holistic\" growth."', effect: () => { usePower.getState().bumpRep('press', -3); return 0; } },
      { label: '"next question."', effect: () => { usePower.getState().bumpRep('cnbc', -2); return -1; } },
    ],
  },
  {
    text: 'ANALYST 2: "guidance for FY?"',
    options: [
      { label: '"raising guidance. significantly."', effect: () => { return 2; } },
      { label: '"we\'re reaffirming guidance."', effect: () => { return 1; } },
      { label: '"we are pulling guidance. due to \"macro uncertainty.\""', effect: () => { return -1; } },
    ],
  },
  {
    text: 'ANALYST 3: "can you address the Wall Street Journal article from yesterday about the auditor walking out?"',
    options: [
      { label: '"the auditor did not walk out. she resigned. those are different."', effect: () => { usePower.getState().bumpRep('press', -4); return 0; } },
      { label: '"that report contained inaccuracies which we have addressed in our 8-K."', effect: () => { useLife.getState().bumpSecHeat(8); return 0; } },
      { label: '"i don\'t answer questions about journalism."', effect: () => { usePower.getState().bumpRep('press', -8); usePower.getState().bumpRep('maga', 5); return -1; } },
    ],
  },
  {
    text: 'ANALYST 4: "what\'s your TAM in the prediction-markets space?"',
    options: [
      { label: '"\$14 trillion."', effect: () => { return 2; } },
      { label: '"if you have to ask about TAM you\'re not the audience for this conversation."', effect: () => { usePower.getState().bumpNotoriety(8); return 1; } },
      { label: '"i\'d love to take that offline."', effect: () => { return 0; } },
    ],
  },
];

let guidanceScore = 0;

export function startEarningsCallArc(): void {
  guidanceScore = 0;
  useMeta.getState().recordLore('arc-earnings-call', 'Earnings Call');
  useCinematic.getState().fire({
    id: `earnings-${Date.now()}`,
    type: 'breaking',
    headline: 'EARNINGS CALL — MYCO HOLDINGS.',
    subhead: '4 analyst questions. you have not read the deck. neither have they. tape will move on tone.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `earnings-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `earnings-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          guidanceScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: usePhone.getState().contacts['cfo'] ? 'cfo' : 'broker',
        fromNpc: usePhone.getState().contacts['cfo'] ? 'cfo' : 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `earnings: Q ${i + 1}/4` });
      sounds.notifyAlert();
    }, 5_000 + i * 25_000);
  }

  setTimeout(() => {
    if (guidanceScore >= 5) {
      useMarkets.getState().addCash(800_000);
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: 0.10 });
      useXClone.getState().receivePost({
        authorHandle: 'CNBCSquawk', authorTribe: 'cnbc',
        text: 'POST-EARNINGS: $MYCO beat on guidance. analysts call it \"a masterclass.\" stock up 9%. one analyst on Twitter has filed his own resignation in protest.',
        likes: 8400, reposts: 2100,
      });
    } else if (guidanceScore <= -1) {
      useMarkets.getState().addCash(-400_000);
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: -0.12 });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'POST-EARNINGS: $MYCO disappointing. CFO did not appear on the call. analysts \"concerned.\" board meeting scheduled.',
        likes: 6400, reposts: 1700,
      });
    } else {
      useNotifications.getState().push({ surfaceId: 'tv', severity: 'info', text: 'earnings call: in line. tape: unmoved. analyst notes pending.' });
    }
  }, 5_000 + BEATS.length * 25_000 + 5_000);
}
